import React from "react";
import { Box, Button, Card, IconButton, Typography } from "@material-ui/core";
import clsx from "clsx";
import moment from "moment";
import EventNoteRoundedIcon from "@material-ui/icons/EventNoteRounded";
import ChevronLeftRoundedIcon from "@material-ui/icons/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@material-ui/icons/ChevronRightRounded";
import { useGridStyles, GRID_COLORS } from "./styles";

const isFollowUp = (appt) =>
  !!(
    appt.followUpId ||
    appt.followupId ||
    appt.follow_up_id ||
    appt.isFollowUp ||
    appt.followUp ||
    appt.FollowUp
  );

const getPatientName = (appt) =>
  appt.Patient
    ? `${appt.Patient.firstName} ${appt.Patient.lastName || ""}`.trim()
    : appt.title || "Unknown";

// Cancelled appointments stay on the grid so the slot history is visible, but
// they are greyed out, struck through and not clickable.
const isCancelled = (appt) =>
  (appt.status || "").toUpperCase().startsWith("CANCEL");

const cancelledTooltip = (appt) =>
  [
    "Cancelled",
    appt.cancellationReason ? `— ${appt.cancellationReason}` : "",
    appt.cancelledBy ? `(by ${appt.cancelledBy.toLowerCase()})` : "",
  ]
    .filter(Boolean)
    .join(" ");

// Working hours come back as timestamps (or "HH:mm"); both are reduced to the
// hour the grid needs.
const hourOf = (value, roundUp = false) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "string" && /^\d{1,2}:\d{2}/.test(value)) {
    const [h, m] = value.split(":").map(Number);
    return roundUp && m > 0 ? h + 1 : h;
  }
  const time = moment(typeof value === "number" ? value : Number(value) || value);
  if (!time.isValid()) return null;
  return roundUp && time.minute() > 0 ? time.hour() + 1 : time.hour();
};

/**
 * The hours the grid draws: the doctor's configured working hours (widest
 * opening -> closing across their working days), so a 10:00-21:00 doctor does
 * not get a grid that stops at 17:30. Falls back to 8-18 only when no working
 * hours are configured, and always widens to cover an appointment booked
 * outside them.
 */
const workingHourRange = (workingHours) => {
  let min = null;
  let max = null;
  (workingHours || []).forEach((item) => {
    if (!item || item.isWorkingDay === false) return;
    const open = hourOf(item.openingTime);
    const close = hourOf(item.closingTime, true);
    if (open === null || close === null || close <= open) return;
    min = min === null ? open : Math.min(min, open);
    max = max === null ? close : Math.max(max, close);
  });
  return min === null ? { min: 8, max: 18 } : { min, max };
};

const WeeklyScheduleGrid = ({
  appointments = [],
  workingHours,
  viewMode,
  anchorDate,
  onPrev,
  onNext,
  onViewChange,
  onChipClick,
  onEmptyClick,
  onDayHeaderClick,
  selectedEventId,
  title = "Weekly Schedule Overview",
}) => {
  const classes = useGridStyles();

  const weekStart = anchorDate.clone().startOf("isoWeek");
  const days =
    viewMode === "week"
      ? Array.from({ length: 7 }, (_, i) => weekStart.clone().add(i, "days"))
      : [anchorDate.clone().startOf("day")];

  const rangeStart = days[0].clone().startOf("day");
  const rangeEnd = days[days.length - 1].clone().endOf("day");
  const visible = appointments.filter((a) => {
    const t = moment(a.startTime);
    return t.isSameOrAfter(rangeStart) && t.isSameOrBefore(rangeEnd);
  });

  const hourRange = workingHourRange(workingHours);
  let minHour = hourRange.min;
  let maxHour = hourRange.max;
  visible.forEach((a) => {
    const h = moment(a.startTime).hour();
    if (h < minHour) minHour = h;
    if (h + 1 > maxHour) maxHour = h + 1;
  });
  const slots = [];
  for (let h = minHour; h < maxHour; h++) {
    slots.push({ h, m: 0 });
    slots.push({ h, m: 30 });
  }

  const cellAppts = (day, h, m) =>
    visible.filter((a) => {
      const t = moment(a.startTime);
      return (
        t.isSame(day, "day") &&
        t.hour() === h &&
        (m === 0 ? t.minute() < 30 : t.minute() >= 30)
      );
    });

  const headerLabel =
    viewMode === "week"
      ? `${days[0].format("MMM DD")} – ${days[6].format("MMM DD, YYYY")}`
      : days[0].format("dddd, MMM DD, YYYY");

  const clickable = typeof onEmptyClick === "function";

  return (
    <Card className={classes.scheduleCard} elevation={0}>
      <Box className={classes.cardHeader}>
        <Box className={classes.headerTitleWrap}>
          <EventNoteRoundedIcon className={classes.headerIcon} />
          <Typography className={classes.headerTitle}>
            {title} ({headerLabel})
          </Typography>
        </Box>

        <Box className={classes.headerControls}>
          <Box className={classes.navGroup}>
            <IconButton
              className={classes.navButton}
              size="small"
              onClick={onPrev}
              aria-label="previous"
            >
              <ChevronLeftRoundedIcon />
            </IconButton>
            <IconButton
              className={classes.navButton}
              size="small"
              onClick={onNext}
              aria-label="next"
            >
              <ChevronRightRoundedIcon />
            </IconButton>
          </Box>

          <Box className={classes.viewToggle}>
            <Button
              disableElevation
              className={clsx(
                classes.viewToggleBtn,
                viewMode === "week" && classes.viewToggleBtnActive
              )}
              onClick={() => onViewChange("week")}
            >
              Week
            </Button>
            <Button
              disableElevation
              className={clsx(
                classes.viewToggleBtn,
                viewMode === "day" && classes.viewToggleBtnActive
              )}
              onClick={() => onViewChange("day")}
            >
              Day
            </Button>
          </Box>
        </Box>
      </Box>

      <Box className={classes.scheduleScroll}>
        <Box
          className={classes.scheduleGrid}
          style={{
            gridTemplateColumns: `90px repeat(${days.length}, minmax(120px, 1fr))`,
          }}
        >
          <Box className={classes.gridHeadCell}>Time Slot</Box>
          {days.map((d) => {
            const isToday = d.isSame(moment(), "day");
            return (
              <Box
                key={`head-${d.format("YYYY-MM-DD")}`}
                className={clsx(
                  classes.dayHeadCell,
                  isToday && classes.todayHeadCell
                )}
                style={onDayHeaderClick ? { cursor: "pointer" } : undefined}
                onClick={
                  onDayHeaderClick ? () => onDayHeaderClick(d) : undefined
                }
              >
                {d.format("ddd")} ({d.format("DD MMM")})
              </Box>
            );
          })}

          {slots.map((slot) => {
            const label = moment()
              .hour(slot.h)
              .minute(slot.m)
              .format("hh:mm A");
            return (
              <React.Fragment key={`row-${slot.h}-${slot.m}`}>
                <Box className={classes.timeCell}>{label}</Box>
                {days.map((d) => {
                  const isToday = d.isSame(moment(), "day");
                  const items = cellAppts(d, slot.h, slot.m);
                  return (
                    <Box
                      key={`cell-${d.format("YYYY-MM-DD")}-${slot.h}-${slot.m}`}
                      className={clsx(
                        classes.slotCell,
                        isToday && classes.todayCol,
                        clickable && classes.slotClickable
                      )}
                      onClick={
                        clickable
                          ? () => onEmptyClick(d, slot.h, slot.m)
                          : undefined
                      }
                    >
                      {items.map((appt) => {
                        const follow = isFollowUp(appt);
                        const cancelled = isCancelled(appt);
                        return (
                          <Box
                            key={appt.id}
                            title={
                              cancelled ? cancelledTooltip(appt) : undefined
                            }
                            className={clsx(
                              classes.chip,
                              cancelled
                                ? classes.chipCancelled
                                : follow
                                ? classes.chipFollow
                                : classes.chipNew,
                              selectedEventId === appt.id &&
                                !cancelled &&
                                classes.chipSelected
                            )}
                            onClick={(e) => {
                              e.stopPropagation();
                              // Cancelled chips are inert — nothing to open or edit.
                              if (cancelled) return;
                              if (onChipClick) onChipClick(appt);
                            }}
                          >
                            <span
                              className={classes.chipDot}
                              style={{
                                backgroundColor: cancelled
                                  ? GRID_COLORS.CANCELLED
                                  : follow
                                  ? GRID_COLORS.FOLLOW
                                  : GRID_COLORS.NEW,
                              }}
                            />
                            <span className={classes.chipName}>
                              {getPatientName(appt)}
                            </span>
                          </Box>
                        );
                      })}
                    </Box>
                  );
                })}
              </React.Fragment>
            );
          })}
        </Box>
      </Box>

      <Box className={classes.legend}>
        <Box className={classes.legendItem}>
          <span
            className={classes.legendDot}
            style={{ backgroundColor: GRID_COLORS.FOLLOW }}
          />
          Follow-up
        </Box>
        <Box className={classes.legendItem}>
          <span
            className={classes.legendDot}
            style={{ backgroundColor: GRID_COLORS.NEW }}
          />
          New Appointment
        </Box>
        <Box className={classes.legendItem}>
          <span
            className={classes.legendDot}
            style={{ backgroundColor: GRID_COLORS.CANCELLED }}
          />
          Cancelled
        </Box>
      </Box>
    </Card>
  );
};

export default WeeklyScheduleGrid;
